// The universal entry timeline's vocabulary: the row shape of the
// entry_timeline_event view (fraud-dbs 20260917000000), one describer per
// source/kind that turns a row into a title, a chip and a one-line summary,
// the milestone derivation the diagram strip uses, and the dedupe rule for
// the events the view deliberately keeps twice (a send is an action event
// AND an email_logs row AND a mitigation_report step; a matched reply is an
// action event AND an email_replies row).
//
// Labels come from the maps the other widgets already own -- STEP_META for
// pipeline steps, describeEvent for action events, TASK_STATUS_LABEL for an
// action's state -- so the timeline never disagrees with the widget it
// points at.

import type { LucideIcon } from 'lucide-react'
import { Inbox, Search, Globe, MonitorSmartphone, Radar, Sparkles, Send, Mail, MessageSquareReply, ShieldCheck } from 'lucide-react'
import { parseDbTimestamp } from '@/lib/dates'
import { VERDICT_LABEL, type CHIP_TONE_CLASS, type CandidateVerdict } from './autoDomainTypes'
import { stepMeta, type Tone } from '../PipelineStepsWidget'
import { describeEvent } from '../EntryDetail/ActionsWidget'
import { TASK_STATUS_LABEL, TASK_STATUS_TONE, RESOLUTION_LABEL, isContained, type ReportTaskStatus, type ReportResolution } from './actionTypes'
import { parseTimelineDate, type BrowserCampaignState, type TimelineSummaryItem } from '../ProcessedEntry/DomainEntry/ProcessedEntryTimeline'

export type TimelineSource = 'entry' | 'detection' | 'domain' | 'browser' | 'probe' | 'candidate' | 'action' | 'email' | 'reply' | 'outcome'
export type ChipTone = keyof typeof CHIP_TONE_CLASS

export type TimelineEvent = {
  user_entry_id: string
  at: string
  source: TimelineSource
  kind: string
  ref_id: string
  action_id: string | null
  channel: string | null
  actor: string | null
  detail: Record<string, any>
  /** Set on the candidate arms; the key the widget uses before promotion. */
  candidate_id?: string | null
}

export type TimelineState = { events: TimelineEvent[]; truncated?: boolean }

export const SOURCE_ORDER: TimelineSource[] = ['entry', 'candidate', 'detection', 'domain', 'browser', 'probe', 'action', 'email', 'reply', 'outcome']

export const SOURCE_META: Record<TimelineSource, { label: string; tone: ChipTone; icon: LucideIcon; dot: string }> = {
  entry:     { label: 'Entry',     tone: 'neutral', icon: Inbox,              dot: 'bg-subtle' },
  candidate: { label: 'Sourcing',  tone: 'sky',     icon: Sparkles,           dot: 'bg-sky-300' },
  detection: { label: 'Detection', tone: 'sky',     icon: Search,             dot: 'bg-signal' },
  domain:    { label: 'Domain',    tone: 'sky',     icon: Globe,              dot: 'bg-indigo-400' },
  browser:   { label: 'Browser',   tone: 'amber',   icon: MonitorSmartphone,  dot: 'bg-warning' },
  probe:     { label: 'Probes',    tone: 'neutral', icon: Radar,              dot: 'bg-subtle' },
  action:    { label: 'Actions',   tone: 'emerald', icon: Send,               dot: 'bg-success' },
  email:     { label: 'Email',     tone: 'emerald', icon: Mail,               dot: 'bg-success text-success-foreground' },
  reply:     { label: 'Replies',   tone: 'amber',   icon: MessageSquareReply, dot: 'bg-orange-400' },
  outcome:   { label: 'Outcome',   tone: 'emerald', icon: ShieldCheck,        dot: 'bg-emerald-500' },
}

// The outcome axis (entry_timeline_event 20261025000000): who stopped the
// host and how. An operator's resolution is confirmed; a provider page or a
// WHOIS hold seen by a probe is proposed, never confirmed by itself.
const OUTCOME_PROPOSER: Record<string, string> = { operator: 'confirmed by an operator', provider_page: "provider's page seen", whois: 'WHOIS record' }
const OUTCOME_BY: Record<string, string> = { host: 'the host', registrar: 'the registrar', registry: 'the registry', cloudflare: 'Cloudflare', google: 'Google', microsoft: 'Microsoft', cncs: 'CNCS' }
export const outcomeLabel = (o: unknown) => (o ? RESOLUTION_LABEL[o as ReportResolution] ?? String(o) : 'Outcome')
const describeOutcome = (e: TimelineEvent): Described => {
  const d = e.detail ?? {}
  const by = d.by ? OUTCOME_BY[d.by] ?? d.by : null
  if (e.kind === 'outcome_lifted') {
    return { title: `Outcome lifted · ${outcomeLabel(d.outcome)}`, tone: 'amber', chip: 'back up',
      summary: [d.proposed_by === 'whois' ? 'a normal WHOIS record again' : 'the real page is back', by ? `was ${by}` : null, d.hostname].filter(Boolean).join(' · ') }
  }
  return { title: `${d.confirmed ? 'Outcome' : 'Outcome observed'} · ${outcomeLabel(d.outcome)}`,
    tone: isContained(d.outcome) ? 'emerald' : d.outcome === 'refused' ? 'red' : 'neutral',
    chip: d.confirmed ? 'confirmed' : 'proposed',
    summary: [by ? `by ${by}` : e.channel ? channelLabel(e.channel) : null, OUTCOME_PROPOSER[d.proposed_by] ?? d.proposed_by,
      d.first_look ? 'already so on our first look' : null,
      d.evidence ? String(d.evidence).replace(/_/g, ' ') : null,
      d.reversible === false ? 'final' : d.reversible ? 'can be undone' : null,
      d.note, d.hostname].filter(Boolean).join(' · ') }
}

// Mirror of the channel catalogue (libs/tooling/mitigation/strategies.py);
// the timeline has no catalogue fetch of its own.
export const CHANNEL_LABEL: Record<string, string> = {
  registrar_abuse: 'Registrar abuse desk', cncs: 'CNCS', google_safe_browsing: 'Google Safe Browsing',
  cloudflare_abuse: 'Cloudflare abuse form', microsoft_report: 'Microsoft SmartScreen', generic_form: 'Abuse web form',
  custom_email: 'Custom email', sibs_alert: 'SIBS fraud alert', sibs_case_report: 'SIBS case report',
  domain_report: 'Domain detection report', alert_email: 'Compose Email', domain_marked_inactive: 'Domain marked inactive',
}
export const channelLabel = (id: string | null | undefined) => (id ? CHANNEL_LABEL[id] ?? id : '')

// How an inbound reply was tied to its thread (email_replies.match_basis).
const MATCH_BASIS_LABEL: Record<string, string> = { ref_tag: 'REF tag', ref_body: 'REF in quote', address: 'report address', header: 'thread', subject: 'subject match', unmatched: 'unmatched' }
// Rows projected from another entry about the same host (inbound_report and
// its thread replies, entry_timeline_event 20261020000001).
const SOURCE_ENTRY_CHIP = 'same host'

export const EVENT_KIND_LABEL: Record<string, string> = {
  planned: 'Channel planned', queued: 'Queued for dispatch', sent: 'Report sent', handoff_parked: 'Parked for the browser pane',
  handoff_fulfilled: 'Form submitted', reply: 'Reply received', status_change: 'Status changed', error: 'Send failed', note: 'Note',
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// An actor worth printing on the row: a machine name (auto_domains,
// qubes_campaigns, auto:brand_impersonation) or an address, never a bare
// user id -- that stays in the row's expanded JSON.
export const actorLabel = (actor: string | null | undefined) => (actor && !UUID_RE.test(actor) ? actor : '')

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// A day-group header from the YYYY-MM-DD key groupByDay produces.
export const dayLabel = (day: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : 'undated'
}
// A registration date as the WHOIS panels print it (day only); the raw
// string when it is not a date we can parse.
export const registeredLabel = (raw: unknown) => {
  const d = parseTimelineDate(String(raw ?? '')) ?? parseDbTimestamp(String(raw ?? ''))
  return d ? `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : String(raw ?? '')
}

const toneOfStep = (t: Tone): ChipTone => (t === 'success' ? 'emerald' : t === 'error' ? 'red' : t === 'active' ? 'sky' : 'neutral')
const upDown = (v: unknown) => (v === true ? 'up' : v === false ? 'down' : 'unknown')
// one render's word: its observation when the render met a challenge (the
// alive flag is NULL then), else the flag
const scenarioWord = (alive: unknown, observation: unknown) =>
  observation === 'challenge_or_captcha' ? 'challenged' : upDown(alive)
// the campaign-loop state of a browser capture: the view's content-rule
// state (up / down / mixed / unknown), except that a capture whose renders
// met a bot challenge is 'blocked' -- the host is there, behind a wall our
// lane did not pass (libs/tooling/observation.py; 2026-10-24). Shared with
// EntryTimelineWidget so the "Now …" chip and the dots say the same.
export const captureState = (d: Record<string, any>): BrowserCampaignState =>
  d.observation === 'challenge_or_captcha' ? 'blocked' : ((d.state ?? 'unknown') as BrowserCampaignState)
const challengeWord = (d: Record<string, any>) => {
  const ch = d.challenge as { provider?: string | null; kind?: string | null } | null | undefined
  if (!ch) return d.observation === 'challenge_or_captcha' ? 'bot challenge' : ''
  return `${ch.provider === 'cloudflare' ? 'Cloudflare' : 'bot'} ${ch.kind === 'waf' ? 'block' : ch.kind === 'forbidden' ? '403' : ch.kind ?? 'challenge'}`
}
const str = (v: unknown) => (v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v))

// "still seen <date>": the certificate logs listed the host again on a
// later run (last_seen_at moves in place; there is no row for it).
const stillSeen = (lastSeen: unknown, sightedAt: string) => {
  if (typeof lastSeen !== 'string' || !lastSeen) return null
  const a = parseDbTimestamp(lastSeen), b = parseDbTimestamp(sightedAt)
  if (!a || !b || a.getTime() - b.getTime() < 60_000) return null
  return `still seen ${registeredLabel(lastSeen)}`
}
const verdictLabel = (v: unknown) => (typeof v === 'string' && v ? (VERDICT_LABEL[v as CandidateVerdict] ?? v) : undefined)
// The analysis lanes (fraud-dbs 20260917000001): the 5G laptop is the ground
// truth, the Azure container through the proxy is bulk tracking, dev_direct
// is an unproxied dev machine. Mirrors VANTAGE_LABEL in autoDomainTypes.
const LANE_LABEL: Record<string, string> = { '5g': '5G', azure_proxy: 'Azure', dev_direct: 'Dev' }
const vantageLabel = (v: unknown) => (typeof v === 'string' && v ? (LANE_LABEL[v] ?? v) : '')
// brand_lure is the alarm; a live site of any other kind is worth a look; dead/unreachable are quiet.
const verdictTone = (v: unknown): ChipTone => (v === 'brand_lure' ? 'red' : v === 'impersonation_other' || v === 'brand_mention' ? 'amber' : v === 'live_other' ? 'sky' : 'neutral')

export type Described = { title: string; summary: string; tone: ChipTone; chip?: string }

export function describeTimelineEvent(e: TimelineEvent): Described {
  const d = e.detail ?? {}
  switch (e.source) {
    case 'entry':
      if (e.kind === 'received') {
        return { title: 'Received', tone: 'neutral', chip: d.post_type ?? undefined,
          summary: [d.sender, d.url, d.source ? `via ${d.source}` : null].filter(Boolean).join(' · ') }
      }
      return { title: 'Reviewed', tone: d.revision_status === 'reject' ? 'red' : 'emerald', chip: d.revision_status ?? undefined, summary: '' }
    case 'candidate':
      switch (e.kind) {
        case 'candidate_sighted': return { title: 'Sighted by auto-domains', tone: 'sky', chip: d.start_kind ?? undefined, summary: [d.hostname, d.brand_position ? `brand ${d.brand_position}` : null, d.quality_score != null ? `quality ${Number(d.quality_score).toFixed(2)}` : null, e.actor ? `run by ${e.actor}` : null, stillSeen(d.last_seen_at, e.at)].filter(Boolean).join(' · ') }
        case 'candidate_analyzed': return { title: 'Analysed', tone: verdictTone(d.verdict), chip: verdictLabel(d.verdict), summary: [d.verdict_score != null ? `score ${Number(d.verdict_score).toFixed(2)}` : null, d.verdict_vantage ? `best from ${vantageLabel(d.verdict_vantage)}` : null, d.is_alive != null ? `site ${d.is_alive ? 'alive' : 'dead'}` : null].filter(Boolean).join(' · ') }
        case 'candidate_pass': return { title: `Analysis pass · ${vantageLabel(d.vantage) || 'lane —'}`, tone: verdictTone(d.verdict), chip: verdictLabel(d.verdict),
          summary: [d.score != null ? `score ${Number(d.score).toFixed(2)}` : null, d.is_alive != null ? `site ${d.is_alive ? 'alive' : 'dead'}` : null, d.tests != null ? `${d.tests} tests${d.errors ? `, ${d.errors} errors` : ''}` : null, Array.isArray(d.layers) ? d.layers.join(', ') : null, d.has_screenshot ? 'screenshot' : null].filter(Boolean).join(' · ') }
        case 'candidate_discarded': return { title: 'Discarded', tone: 'neutral', chip: d.discarded_reason ?? undefined, summary: d.review_note ?? '' }
        case 'candidate_whois': return { title: 'WHOIS checked', tone: 'sky', summary: [d.registrar, d.registered === false ? 'not registered' : null, d.abuse_email ? `abuse ${d.abuse_email}` : null, d.hosting_is_cloudflare ? 'behind Cloudflare' : null].filter(Boolean).join(' · ') }
        case 'candidate_promoted': return { title: 'Promoted to an entry', tone: 'emerald', summary: e.actor ?? '' }
        default: return { title: e.kind, tone: 'neutral', summary: '' }
      }
    case 'detection': {
      const m = stepMeta(e.kind)
      const parts: string[] = []
      if (d.is_scam != null) parts.push(d.is_scam === true || d.is_scam === 'true' ? 'SCAM' : 'not a scam')
      if (d.verdict_label) parts.push(str(d.verdict_label))
      if (d.scam_value != null) parts.push(`score ${str(d.scam_value)}`)
      if (d.confidence) parts.push(`confidence ${str(d.confidence)}`)
      if (d.domain) parts.push(str(d.domain))
      if (d.error) parts.push(str(d.error))
      if (d.note) parts.push(str(d.note))
      return { title: m.label, tone: toneOfStep(m.tone), chip: d.is_scam != null ? (d.is_scam === true || d.is_scam === 'true' ? 'scam' : 'clean') : undefined, summary: parts.join(' · ') }
    }
    case 'domain':
      if (e.kind === 'domain_linked') return { title: 'Domain linked', tone: 'sky', summary: [d.hostname, d.original_url].filter(Boolean).join(' · ') }
      return { title: 'WHOIS / domain snapshot', tone: 'sky',
        chip: d.is_active != null ? (d.is_active === true || d.is_active === 'true' ? 'active' : 'inactive') : undefined,
        summary: [d.registrar ? `registrar ${d.registrar}` : null, d.create_date ? `registered ${registeredLabel(d.create_date)}` : null, d.ip_address ? `IP ${d.ip_address}` : null, d.asn ? `ASN ${d.asn}` : null].filter(Boolean).join(' · ') }
    case 'browser': {
      const st = captureState(d)
      return { title: 'Browser capture', tone: st === 'up' ? 'emerald' : st === 'down' ? 'red' : st === 'mixed' || st === 'blocked' ? 'amber' : 'neutral',
        chip: st === 'blocked' ? 'CHALLENGE' : st.toUpperCase(),
        summary: `Mobile ${scenarioWord(d.mobile_is_alive, d.observation_mobile)} · Desktop ${scenarioWord(d.desktop_is_alive, d.observation_desktop)}${st === 'blocked' ? ` · ${challengeWord(d)}` : ''}${d.r2_key ? ' · Image available' : ''}` }
    }
    case 'probe':
      return { title: 'Probe run', tone: d.dead && !d.alive ? 'red' : d.alive ? 'emerald' : 'neutral',
        summary: [d.probe_node, d.tests != null ? `${d.alive ?? 0} alive / ${d.dead ?? 0} dead of ${d.tests}` : null, Array.isArray(d.layers) ? d.layers.join(', ') : null, e.actor ? `by ${e.actor}` : null].filter(Boolean).join(' · ') }
    case 'action': {
      const ch = channelLabel(e.channel)
      if (e.kind === 'action_created') {
        const status = d.status as ReportTaskStatus | undefined
        return { title: `Action opened · ${ch}`, tone: status ? TASK_STATUS_TONE[status] : 'neutral', chip: status ? TASK_STATUS_LABEL[status] : undefined,
          summary: [d.about ?? d.hostname, d.target ? `→ ${d.target}` : null, d.resolution ? RESOLUTION_LABEL[d.resolution as ReportResolution] : null].filter(Boolean).join(' · ') }
      }
      if (e.kind === 'mitigation_report') {
        return { title: `Report sent · ${ch}`, tone: 'emerald',
          summary: d.form_url ? `${d.form_url}${d.confirmation_text ? ` — ${d.confirmation_text}` : ''}` : `to ${(d.to ?? []).join(', ')}${d.subject ? ` — ${d.subject}` : ''}` }
      }
      const tone: ChipTone = e.kind === 'error' ? 'red' : e.kind === 'sent' || e.kind === 'handoff_fulfilled' ? 'emerald' : e.kind === 'reply' ? 'amber' : e.kind === 'status_change' && d.to === 'cancelled' ? 'neutral' : e.kind === 'status_change' && d.to === 'resolved' ? 'emerald' : 'sky'
      return { title: `${EVENT_KIND_LABEL[e.kind] ?? e.kind} · ${ch}`, tone, summary: describeEvent(e.kind, d) }
    }
    case 'email':
      // inbound_report: another entry, created from an inbound email about the
      // same host (a vendor feed such as Claranet's), shown on this candidate.
      if (e.kind === 'inbound_report') {
        return { title: 'Reported by email', tone: 'sky', chip: SOURCE_ENTRY_CHIP,
          summary: [d.from_address, d.subject, d.source_user_entry_id ? `entry ${String(d.source_user_entry_id).slice(0, 8)}` : null].filter(Boolean).join(' — ') }
      }
      return { title: `${e.kind === 'email_sent' ? 'Email sent' : 'Email failed'} · ${channelLabel(e.channel)}`, tone: e.kind === 'email_sent' ? 'emerald' : 'red', chip: d.campaign ?? undefined,
        summary: [`to ${(d.to ?? []).join(', ')}`, d.subject, d.error].filter(Boolean).join(' — ') }
    case 'reply':
      // With an action: the answer to that report. Without one: a reply to a
      // send no action tracks (log_id), or a reply in an inbound email thread.
      return { title: e.action_id ? `Reply · ${channelLabel(e.channel)}` : d.log_id ? 'Reply to a report' : 'Thread reply', tone: 'amber',
        chip: d.source_user_entry_id ? SOURCE_ENTRY_CHIP : d.match_basis ? MATCH_BASIS_LABEL[d.match_basis] ?? d.match_basis : undefined,
        summary: [d.from_address, d.subject, d.excerpt, d.source_user_entry_id ? `entry ${String(d.source_user_entry_id).slice(0, 8)}` : null].filter(Boolean).join(' — ') }
    case 'outcome':
      return describeOutcome(e)
    default:
      return { title: e.kind, tone: 'neutral', summary: '' }
  }
}

// The events the view keeps twice on purpose, collapsed for reading:
//  * a `mitigation_report` step is hidden when an action event with the
//    same action_id exists (the event carries the same facts);
//  * an action `reply` event is hidden when an email_replies row for the
//    same action sits within a minute of it (the row has the excerpt);
//  * a `planned` event is hidden when the action's own `action_created`
//    row sits within a minute of it -- the planner writes both in the same
//    transaction and the created row already names the target;
//  * `candidate_analyzed` (the best-verdict stamp) is hidden when a
//    `candidate_pass` row sits within a minute of it -- the pass is the
//    same moment with the lane on it; the best verdict stays on the strip;
//  * a host with several candidate rows from the pre-2026-09-17 sighting
//    model (a re-sighting copied promoted_at / analyzed_at from the row
//    before it) yields the same candidate event twice at the same second;
//    the second copy is dropped.
export function dedupe(events: TimelineEvent[]): TimelineEvent[] {
  const actionIdsWithEvents = new Set(events.filter((e) => e.source === 'action' && e.kind !== 'action_created' && e.kind !== 'mitigation_report' && e.action_id).map((e) => e.action_id))
  const replyRows = events.filter((e) => e.source === 'reply')
  const createdAt = new Map(events.filter((e) => e.kind === 'action_created' && e.action_id).map((e) => [e.action_id, new Date(e.at).getTime()]))
  const near = (a: number | undefined, b: string) => a != null && Math.abs(a - new Date(b).getTime()) < 60_000
  const passTimes = events.filter((e) => e.kind === 'candidate_pass').map((e) => new Date(e.at).getTime())
  const seenCandidateEvent = new Set<string>()
  return events.filter((e) => {
    if (e.source === 'candidate' && e.kind !== 'candidate_pass') {
      const key = `${e.kind}:${e.detail?.hostname ?? ''}:${Math.floor(new Date(e.at).getTime() / 1000)}`
      if (seenCandidateEvent.has(key)) return false
      seenCandidateEvent.add(key)
    }
    if (e.kind === 'mitigation_report' && e.action_id && actionIdsWithEvents.has(e.action_id)) return false
    if (e.source === 'action' && e.kind === 'planned' && e.action_id && near(createdAt.get(e.action_id), e.at)) return false
    if (e.kind === 'candidate_analyzed' && passTimes.some((t) => near(t, e.at))) return false
    if (e.source === 'action' && e.kind === 'reply') {
      const t = new Date(e.at).getTime()
      if (replyRows.some((r) => r.action_id === e.action_id && Math.abs(new Date(r.at).getTime() - t) < 60_000)) return false
    }
    return true
  })
}

export type DayGroup = { day: string; events: TimelineEvent[] }

export function groupByDay(events: TimelineEvent[]): DayGroup[] {
  const groups: DayGroup[] = []
  for (const e of events) {
    const d = parseDbTimestamp(e.at) ?? new Date(e.at)
    const day = Number.isNaN(d.getTime()) ? 'unknown' : d.toISOString().slice(0, 10)
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.events.push(e)
    else groups.push({ day, events: [e] })
  }
  return groups
}

const at = (e: TimelineEvent | undefined) => (e ? parseDbTimestamp(e.at) : null)
const isClassifySuccess = (k: string) => k === 'classify_success' || k === 'classify_retry_success' || k.startsWith('classify_manual_retry_success')
const isScam = (d: Record<string, any>) => d.is_scam === true || d.is_scam === 'true'

// The milestone strip: the handful of moments that tell the story, in the
// TimelineSummaryItem shape the Domain intelligence diagram already draws.
// `state` picks the node colour (up = green, down = red, mixed = amber,
// unknown = slate), reused here as good / bad / attention / neutral.
export function deriveMilestones(events: TimelineEvent[]): TimelineSummaryItem[] {
  const sorted = [...events].sort((a, b) => a.at.localeCompare(b.at))
  const first = (pred: (e: TimelineEvent) => boolean) => sorted.find(pred)
  const last = (pred: (e: TimelineEvent) => boolean) => [...sorted].reverse().find(pred)
  const items: (TimelineSummaryItem | null)[] = []

  const received = first((e) => e.kind === 'received')
  const sighted = first((e) => e.kind === 'candidate_sighted')
  const detected = sighted && (!received || sighted.at < received.at) ? sighted : received
  if (detected) {
    const dt = at(detected)
    if (dt) items.push({ id: 'detected', label: detected === sighted ? 'Detected' : 'Received', value: detected === sighted ? 'SOURCED' : (detected.detail.post_type ?? 'REPORT').toString().toUpperCase(), date: dt, state: 'unknown',
      detail: detected === sighted ? `${detected.detail.hostname ?? ''} found by auto-domains` : [detected.detail.sender, detected.detail.url].filter(Boolean).join(' · ') || 'Entry created' })
  }

  // The candidate's own verdict: the best-verdict stamp when it survived
  // dedupe, else the strongest pass (brand_lure first, then the latest).
  const analysed = first((e) => e.kind === 'candidate_analyzed') ?? [...sorted].filter((e) => e.kind === 'candidate_pass').sort((a, b) => (b.detail.verdict === 'brand_lure' ? 1 : 0) - (a.detail.verdict === 'brand_lure' ? 1 : 0) || b.at.localeCompare(a.at))[0]
  const adt = at(analysed)
  if (analysed && adt) {
    const v = analysed.detail.verdict
    items.push({ id: 'analysed', label: 'Analysed', value: (verdictLabel(v) ?? 'PENDING').toUpperCase(), date: adt,
      state: v === 'brand_lure' ? 'mixed' : v === 'dead' || v === 'unreachable' ? 'down' : v === 'blocked' ? 'blocked' : v ? 'up' : 'unknown',
      detail: [analysed.detail.verdict_vantage ?? analysed.detail.vantage ? `from ${vantageLabel(analysed.detail.verdict_vantage ?? analysed.detail.vantage)}` : null,
        analysed.detail.observation === 'challenge_or_captcha' ? 'site behind a bot challenge' : analysed.detail.is_alive != null ? `site ${analysed.detail.is_alive ? 'alive' : 'dead'}` : null,
        analysed.detail.evasive ? 'evasive' : null].filter(Boolean).join(' · ') || 'Auto-domains verdict' })
  }

  const promoted = first((e) => e.kind === 'candidate_promoted')
  const pdt = at(promoted)
  if (promoted && pdt) items.push({ id: 'promoted', label: 'Promoted', value: 'ENTRY', date: pdt, state: 'up', detail: promoted.actor ? `by ${promoted.actor}` : 'Became an entry' })

  const discarded = first((e) => e.kind === 'candidate_discarded')
  const ddt = at(discarded)
  if (discarded && ddt) items.push({ id: 'discarded', label: 'Discarded', value: String(discarded.detail.discarded_reason ?? 'DISCARDED').toUpperCase(), date: ddt, state: 'down', detail: discarded.detail.review_note ?? 'Closed as a candidate' })

  const classified = first((e) => e.source === 'detection' && isClassifySuccess(e.kind))
  const cdt = at(classified)
  if (classified && cdt) {
    const scam = isScam(classified.detail)
    items.push({ id: 'classified', label: 'Classified', value: scam ? 'SCAM' : 'CLEAN', date: cdt, state: scam ? 'mixed' : 'up',
      detail: [classified.detail.verdict_label, classified.detail.domain].filter(Boolean).join(' · ') || stepMeta(classified.kind).label })
  }

  const snapshot = first((e) => e.kind === 'domain_snapshot' && e.detail.create_date)
  const whois = first((e) => e.kind === 'candidate_whois' && e.detail.whois_created_at)
  if (snapshot) {
    const raw = String(snapshot.detail.create_date)
    const parsed = parseTimelineDate(raw)
    items.push({ id: 'domain', label: 'Domain registered', value: 'DOMAIN', date: parsed ?? at(snapshot) ?? new Date(), rawLabel: parsed ? registeredLabel(raw) : raw, state: 'unknown',
      detail: snapshot.detail.registrar ? `Registrar: ${snapshot.detail.registrar}` : 'Registration record' })
  } else if (whois) {
    const d = parseDbTimestamp(String(whois.detail.whois_created_at))
    if (d) items.push({ id: 'domain', label: 'Domain registered', value: 'DOMAIN', date: d, rawLabel: registeredLabel(whois.detail.whois_created_at), state: 'unknown', detail: whois.detail.registrar ? `Registrar: ${whois.detail.registrar}` : 'Registration record' })
  }

  const captures = sorted.filter((e) => e.kind === 'browser_capture')
  const capDetail = (e: TimelineEvent) => `Mobile ${scenarioWord(e.detail.mobile_is_alive, e.detail.observation_mobile)} · Desktop ${scenarioWord(e.detail.desktop_is_alive, e.detail.observation_desktop)}${captureState(e.detail) === 'blocked' ? ` · ${challengeWord(e.detail)}` : ''}${e.detail.r2_key ? ' · Image available' : ''}`
  const capValue = (e: TimelineEvent) => { const s = captureState(e.detail); return s === 'blocked' ? 'CHALLENGE' : s.toUpperCase() }
  const firstCap = captures[0]
  if (firstCap && at(firstCap)) items.push({ id: 'first-capture', label: 'First capture', value: capValue(firstCap), date: at(firstCap)!, state: captureState(firstCap.detail), detail: capDetail(firstCap) })
  const firstUp = captures.find((e) => captureState(e.detail) === 'up' || captureState(e.detail) === 'mixed')
  if (firstUp && firstUp !== firstCap && at(firstUp)) items.push({ id: 'first-up', label: 'First up', value: capValue(firstUp), date: at(firstUp)!, state: captureState(firstUp.detail), detail: capDetail(firstUp) })
  // a challenged capture is never "down": the host answered, behind a wall
  const firstDown = captures.find((e) => captureState(e.detail) === 'down')
  if (firstDown && firstDown !== firstCap && at(firstDown)) items.push({ id: 'first-down', label: 'First down', value: 'DOWN', date: at(firstDown)!, state: 'down', detail: capDetail(firstDown) })
  const firstChallenge = captures.find((e) => captureState(e.detail) === 'blocked')
  if (firstChallenge && firstChallenge !== firstCap && at(firstChallenge)) items.push({ id: 'first-challenge', label: 'First challenge', value: 'CHALLENGE', date: at(firstChallenge)!, state: 'blocked', detail: capDetail(firstChallenge) })

  const sent = first((e) => (e.source === 'action' && (e.kind === 'sent' || e.kind === 'handoff_fulfilled' || e.kind === 'mitigation_report')) || e.kind === 'email_sent')
  const sdt = at(sent)
  if (sent && sdt) items.push({ id: 'reported', label: 'First report sent', value: 'REPORTED', date: sdt, state: 'up', detail: `${channelLabel(sent.channel)}${sent.detail.to ? ` → ${(sent.detail.to as string[]).join(', ')}` : sent.detail.form_url ? ' (web form)' : ''}` })

  const reply = first((e) => e.source === 'reply')
  const rdt = at(reply)
  if (reply && rdt) items.push({ id: 'reply', label: 'First reply', value: 'REPLY', date: rdt, state: 'mixed', detail: [channelLabel(reply.channel), reply.detail.from_address].filter(Boolean).join(' · ') })

  const resolved = first((e) => e.source === 'action' && e.kind === 'status_change' && e.detail?.to === 'resolved')
  const takenDown = first((e) => e.kind === 'action_created' && e.detail?.resolution === 'taken_down')
  const res = resolved ?? takenDown
  const resDt = resolved ? at(resolved) : takenDown?.detail?.resolved_at ? parseDbTimestamp(String(takenDown.detail.resolved_at)) : null
  if (res && resDt) {
    const resolution = (resolved?.detail?.resolution ?? takenDown?.detail?.resolution) as ReportResolution | undefined
    items.push({ id: 'resolved', label: 'Resolved', value: resolution ? RESOLUTION_LABEL[resolution].toUpperCase() : 'RESOLVED', date: resDt, state: isContained(resolution) ? 'up' : resolution === 'refused' ? 'down' : 'mixed', detail: channelLabel(res.channel) })
  }

  // The outcome axis: the first time a probe saw the host stopped (a
  // provider page, a WHOIS hold), and -- when the latest outcome event lifts
  // it -- the moment it came back. An operator's resolution is "Resolved" above.
  const outcomes = sorted.filter((e) => e.source === 'outcome' && e.detail?.proposed_by !== 'operator')
  const contained = outcomes.find((e) => e.kind === 'outcome' && isContained(e.detail?.outcome))
  if (contained && at(contained)) {
    const d = contained.detail
    items.push({ id: 'contained', label: d.first_look ? 'Contained (first look)' : 'Contained', value: outcomeLabel(d.outcome).toUpperCase(), date: at(contained)!, state: 'up',
      detail: [d.by ? `by ${OUTCOME_BY[d.by] ?? d.by}` : null, OUTCOME_PROPOSER[d.proposed_by] ?? d.proposed_by, 'not confirmed'].filter(Boolean).join(' · ') })
  }
  const lastOutcome = outcomes[outcomes.length - 1]
  if (contained && lastOutcome?.kind === 'outcome_lifted' && at(lastOutcome)) {
    items.push({ id: 'outcome-lifted', label: 'Back up', value: 'LIFTED', date: at(lastOutcome)!, state: 'down',
      detail: `${outcomeLabel(lastOutcome.detail?.outcome)} lifted · ${lastOutcome.detail?.proposed_by === 'whois' ? 'normal WHOIS record' : 'real page back'}` })
  }

  // A candidate's monitor passes are its captures before promotion: the
  // strip gets "First down" / "Latest" from them the way an entry gets them
  // from browser captures (a pass says whether the site was alive).
  // (a challenged pass has is_alive NULL -- unknown, not down -- and still counts as a look)
  const challenged = (e: TimelineEvent) => e.detail.observation === 'challenge_or_captcha'
  const passes = captures.length ? [] : sorted.filter((e) => e.kind === 'candidate_pass' && (e.detail.is_alive != null || challenged(e)))
  const passDetail = (e: TimelineEvent) => `${vantageLabel(e.detail.vantage) || 'lane —'} · ${e.detail.verdict ? verdictLabel(e.detail.verdict) : 'no verdict'}${challenged(e) ? ` · ${e.detail.blocked_reason ? String(e.detail.blocked_reason).replace(/_/g, ' ') : 'bot challenge'}` : ''}${e.detail.evasive ? ' · evasive' : ''}`
  const passState = (e: TimelineEvent): BrowserCampaignState => (challenged(e) ? 'blocked' : e.detail.is_alive ? 'up' : 'down')
  const passValue = (e: TimelineEvent) => (challenged(e) ? 'CHALLENGE' : e.detail.is_alive ? 'UP' : 'DOWN')
  const firstDownPass = passes.find((e) => e.detail.is_alive === false)
  if (firstDownPass && firstDownPass !== passes[0] && at(firstDownPass)) items.push({ id: 'first-down', label: 'First down', value: 'DOWN', date: at(firstDownPass)!, state: 'down', detail: passDetail(firstDownPass) })
  const lastPass = passes[passes.length - 1]
  if (lastPass && passes.length > 1 && lastPass !== firstDownPass && at(lastPass)) items.push({ id: 'latest-pass', label: 'Latest check', value: passValue(lastPass), date: at(lastPass)!, state: passState(lastPass), detail: passDetail(lastPass) })

  const lastCap = captures[captures.length - 1]
  const inactive = last((e) => e.kind === 'action_created' && e.channel === 'domain_marked_inactive' && e.detail?.status !== 'cancelled')
  if (inactive && at(inactive)) items.push({ id: 'site-down', label: 'Site down', value: 'MARKED INACTIVE', date: at(inactive)!, state: 'down', detail: inactive.actor ? `by ${inactive.actor}` : 'Request takedown flag' })
  else if (lastCap && lastCap !== firstCap && lastCap !== firstDown && captureState(lastCap.detail) === 'down' && at(lastCap)) items.push({ id: 'site-down', label: 'Latest capture', value: 'DOWN', date: at(lastCap)!, state: 'down', detail: capDetail(lastCap) })
  else if (lastCap && lastCap !== firstCap && lastCap !== firstChallenge && captureState(lastCap.detail) === 'blocked' && at(lastCap)) items.push({ id: 'latest-challenge', label: 'Latest capture', value: 'CHALLENGE', date: at(lastCap)!, state: 'blocked', detail: capDetail(lastCap) })

  return items.filter((i): i is TimelineSummaryItem => i != null).sort((a, b) => a.date.getTime() - b.date.getTime())
}

export const inFlightAction = (e: TimelineEvent) =>
  e.kind === 'action_created' && ['queued', 'sending', 'awaiting_browser'].includes(e.detail?.status)
